import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cloud, Server, Database, Mail, Bot, Cog, Loader2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLATFORMS = [
  { id: "salesforce", icon: Cloud, title: "Salesforce", desc: "Data Cloud, Marketing Cloud (SFMC), MC Next (Agentforce) & Admin + Dev tracks.", color: "indigo" },
  { id: "servicenow", icon: Server, title: "ServiceNow", desc: "Platform Fundamentals, Developer, ITSM, AI & Automation, Certification Prep.", color: "emerald" },
];

const SALESFORCE_TRACKS = [
  { id: "data_cloud", icon: Database, title: "Data Cloud", desc: "Unify data, resolve identity, build segments & activate.", path: "/roadmap" },
  { id: "sfmc", icon: Mail, title: "Marketing Cloud (SFMC)", desc: "Journeys, Automation Studio, AMPscript & SQL.", path: "/sfmc" },
  { id: "mc_next", icon: Bot, title: "MC Next", desc: "Agentforce, Data Cloud + agentic marketing.", path: "/mc-next" },
  { id: "admin_dev", icon: Cog, title: "Admin + Dev", desc: "Objects, security, automation, Apex & deployment.", path: "/admin-dev" },
];

/** New onboarding step (separate from the existing goal-based OnboardingModal)
 * asking the user which platform/track they're here for, right after signup.
 * Saves the preference via PUT /api/users/me/track-preference and redirects
 * straight into that track's Roadmap. */
export function TrackPreferenceModal() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(() => sessionStorage.getItem("track_pref_step") || "platform");
  const [saving, setSaving] = useState(null);

  if (!user || user.role !== "user" || user.track_onboarding_completed) return null;

  const goToStep = (next) => {
    sessionStorage.setItem("track_pref_step", next);
    setStep(next);
  };

  const choose = async (track, path) => {
    setSaving(track);
    try {
      await api.put("/users/me/track-preference", { track });
      sessionStorage.removeItem("track_pref_step");
      await refreshUser();
      navigate(path);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="track-preference-modal">
      <div className="glass-panel rounded-2xl p-8 max-w-lg w-full border border-emerald-400/20">
        {step === "platform" ? (
          <>
            <div className="text-center mb-6">
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-white">Which platform brings you here, {user.name?.split(" ")[0]}?</h2>
              <p className="text-sm text-slate-400 mt-2">We'll take you straight to the right roadmap.</p>
            </div>
            <div className="space-y-3">
              {PLATFORMS.map((p) => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.id}
                    onClick={() => (p.id === "servicenow" ? choose("servicenow", "/servicenow") : goToStep("salesforce_track"))}
                    disabled={!!saving}
                    data-testid={`track-preference-platform-${p.id}`}
                    className="w-full flex items-center gap-4 text-left rounded-xl p-4 border border-white/10 bg-white/5 hover:border-emerald-400/30 hover:bg-white/[0.07] transition-colors disabled:opacity-60"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                      {saving === p.id ? <Loader2 className="w-4 h-4 text-emerald-300 animate-spin" /> : <Icon className="w-5 h-5 text-emerald-300" />}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{p.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{p.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <button onClick={() => goToStep("platform")} data-testid="track-preference-back-btn" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <div className="text-center mb-6">
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-white">Which Salesforce track?</h2>
              <p className="text-sm text-slate-400 mt-2">Pick your starting point — you can explore the others anytime from the Navbar.</p>
            </div>
            <div className="space-y-3">
              {SALESFORCE_TRACKS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => choose(t.id, t.path)}
                    disabled={!!saving}
                    data-testid={`track-preference-salesforce-${t.id}`}
                    className="w-full flex items-center gap-4 text-left rounded-xl p-4 border border-white/10 bg-white/5 hover:border-indigo-400/30 hover:bg-white/[0.07] transition-colors disabled:opacity-60"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                      {saving === t.id ? <Loader2 className="w-4 h-4 text-indigo-300 animate-spin" /> : <Icon className="w-5 h-5 text-indigo-300" />}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{t.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{t.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
